import type { TOC } from '@ember/component/template-only'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type StudentsStepIndexController from 'teacher-ui-client/manage/rollover/steps/students/index/controller'
import { ExpandBox } from 'teacher-ui-client/components/expand-box'
import RouteTemplate from 'ember-route-template'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import { on } from '@ember/modifier'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'

interface Signature {
  Args: {
    controller: StudentsStepIndexController
  }
}
export const ManageRolloverStepsStudentsIndexPage: TOC<Signature> = <template>
  <div class="mx-auto max-w-3xl px-2">
    <div class="mb-12">
      <h3 class="text-dusty-black-250 mt-8 mb-6">
        {{t "manage.rollover.steps.students.stepContentHeader"}}
      </h3>
      <h3 class="text-dusty-black-500 mt-8 mb-6">
        {{t "manage.rollover.steps.students.stepContentSubheader"}}
      </h3>
      <div class="space-y-6 text-xl">
        <p>
          {{t "manage.rollover.steps.students.stepDescription"}}
        </p>
        <p>
          {{#if @controller.studentCsvMaker.downloadRolloverCsv.isIdle}}
            <PhosphorIcon @icon="phosphor-download-fill" class={{@controller.iconColorClass}} />
          {{else}}
            <PhosphorIcon @icon="phosphor-circle-notch-bold" class="h-8 w-8 animate-spin" />
          {{/if}}
          <span
            class="font-bold text-black underline hover:text-black focus:text-black"
            role="button"
            {{on "click" @controller.downloadRolloverCsv}}
          >{{t "manage.rollover.steps.students.export.downloadLink"}}</span>
          {{t "manage.rollover.steps.students.export.downloadDescription"}}
        </p>
      </div>
    </div>

    <div class="mb-12">
      <p class="mb-3 text-xl">
        <span class="font-bold">{{t "manage.rollover.steps.students.importantNotes.title"}}:</span>
        <ul>
          <li class="mt-2">- {{t "manage.rollover.steps.students.importantNotes.note1" htmlSafe=true}}</li>
          <li class="mt-2">- {{t "manage.rollover.steps.students.importantNotes.note2" htmlSafe=true}}</li>
          <li class="mt-2">- {{t "manage.rollover.steps.students.importantNotes.note3"}}</li>
        </ul>
      </p>
    </div>

    <ExpandBox class="mb-6">
      <:header as |collapsed|>
        <div class="bg-juicy-orange-100 flex items-center p-3">
          <PhosphorIcon @icon={{if collapsed "phosphor-caret-down-bold" "phosphor-caret-up-bold"}} class="mr-3" />
          <h3 class="text-dusty-black-500 m-0">
            {{t "manage.rollover.steps.students.howToEdit.title"}}
          </h3>
        </div>
      </:header>
      <:content>
        <div class="bg-juicy-orange-50 space-y-6 p-3 text-xl">
          <p>
            {{t "manage.rollover.steps.students.howToEdit.description"}}
            <p>{{t "manage.rollover.steps.students.howToEdit.descriptionWarning"}}</p>
            <p class="mb-3">{{t "manage.rollover.steps.students.howToEdit.csvDescription"}}:</p>
          </p>
          <BasicTable
            class="w-full text-base"
            @rows={{@controller.studentExampleTableData.rows}}
            @columns={{@controller.studentExampleTableData.columns}}
            as |bt|
          >
            <bt.head />
            <bt.body />
          </BasicTable>
          <p>
            <span class="font-bold">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fields.studentId"
              }}</span>
            <p class="mb-3">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.studentId.content"
              }}</p>
            <p class="mb-3">
              <span class="font-bold">{{t "manage.rollover.steps.students.note"}}</span>:
              {{t "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.studentId.note"}}
            </p>
          </p>
          <p>
            <span class="font-bold">
              {{t "manage.rollover.steps.students.howToEdit.exampleTable.fields.firstName"}}
              /
              {{t "manage.rollover.steps.students.howToEdit.exampleTable.fields.lastName"}}
            </span>
            <p class="mb-3">{{t "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.name"}}</p>
          </p>
          <p>
            <span class="font-bold">{{t "grade.label"}}</span>
            <p class="mb-3">
              {{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.grade"
                gradeLabel=(t "grade.label")
                manageStudents=(t "manage.rollover.steps.students.manageStudents")
              }}
            </p>
          </p>
          <p>
            <span class="font-bold">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fields.teacherEmail"
              }}</span>
            <p class="mb-3">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.addToCreateClass"
              }}</p>
            <p class="mb-3">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.mustHaveAccount"
              }}</p>
            <p class="mb-3">
              {{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.useExportedTeacherCsv"
                teachersStepHeader=(t "manage.rollover.steps.teachers.stepContentHeader")
                htmlSafe=true
              }}
            </p>
            <p class="mb-3">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.useManageTeachers"
                manageTeachers=(t "manage.rollover.steps.students.manageTeachers")
              }}</p>
            <p class="mb-3">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.canLeaveBlank"
              }}</p>
            <p class="mb-3">
              <span class="font-bold">{{t "manage.rollover.steps.students.note"}}</span>:
              {{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.teacherEmail.newClassWillBeCreated"
              }}
            </p>
          </p>
          <p>
            <span class="font-bold">{{t
                "manage.rollover.steps.students.howToEdit.exampleTable.fields.externalId"
              }}</span>
            <p>{{t "manage.rollover.steps.students.howToEdit.exampleTable.fieldDescriptions.externalId"}}</p>
          </p>
        </div>
      </:content>
    </ExpandBox>

    <ExpandBox class="mb-6">
      <:header as |collapsed|>
        <div class="bg-juicy-orange-100 flex items-center p-3">
          <PhosphorIcon @icon={{if collapsed "phosphor-caret-down-bold" "phosphor-caret-up-bold"}} class="mr-3" />
          <h3 class="text-dusty-black-500 m-0">
            {{t "manage.rollover.steps.students.removingRows.title"}}
          </h3>
        </div>
      </:header>
      <:content>
        <div class="bg-juicy-orange-50 p-3 text-xl">
          <p>
            {{t "manage.rollover.steps.students.removingRows.content"}}
          </p>
        </div>
      </:content>
    </ExpandBox>

    <ExpandBox class="mb-6">
      <:header as |collapsed|>
        <div class="bg-juicy-orange-100 flex items-center p-3">
          <PhosphorIcon @icon={{if collapsed "phosphor-caret-down-bold" "phosphor-caret-up-bold"}} class="mr-3" />
          <h3 class="text-dusty-black-500 m-0">
            {{t "manage.rollover.steps.students.newRows.title"}}
          </h3>
        </div>
      </:header>
      <:content>
        <div class="bg-juicy-orange-50 space-y-6 p-3 text-xl">
          <p>
            {{t "manage.rollover.steps.students.newRows.toCreate"}}
          </p>
          <p>
            {{t "manage.rollover.steps.students.newRows.leaveIdBlank"}}
          </p>
          <p>
            {{t "manage.rollover.steps.students.newRows.fillInDetails"}}
          </p>
          <p>
            <span class="font-bold">{{t "manage.rollover.steps.students.note"}}</span>:
            {{t "manage.rollover.steps.students.newRows.loginsAndPasswords"}}
          </p>
        </div>
      </:content>
    </ExpandBox>
    <section class="text-right">
      <label for="upload-csv-input" data-test-upload-students-csv>
        <input
          id="upload-csv-input"
          class="hidden"
          type="file"
          accept="text/csv, .csv"
          {{on "change" @controller.selectCsvFile}}
        />
        <UiButtonLink class="whitespace-nowrap" @appearance={{ButtonAppearance.regular}}>
          {{#if @controller.hasSuccessfulImport}}
            {{t "manage.rollover.steps.students.uploadAnother"}}
          {{else}}
            {{t "manage.rollover.steps.students.upload"}}
          {{/if}}
        </UiButtonLink>
      </label>
    </section>
  </div>
</template>

export default RouteTemplate(ManageRolloverStepsStudentsIndexPage)
